import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, Info, Video } from "lucide-react";
import "../../styles/components/OpinionForm.css";
import API_BASE, { getCsrfToken, withApiCredentials } from "../../api";
import CreatePostSection from "./CreatePostSection";
import {
  getOrCreatePostSubmissionRequestId,
  resetPostSubmissionRequestId,
} from "../../utils/postSubmissionRequestId";

function OpinionForm({ onClose, onAddPost, onSubmittingChange, onDirtyChange }) {
  const { t } = useTranslation();
  const [caption, setCaption] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const clientRequestIdRef = useRef("");
  const resetSubmissionState = useCallback(
    (resetLocalState = true) => {
      submitLockRef.current = false;
      resetPostSubmissionRequestId(clientRequestIdRef);
      onSubmittingChange?.(false);

      if (resetLocalState) {
        setIsSubmitting(false);
      }
    },
    [onSubmittingChange]
  );
  const trimmedCaption = caption.trim();
  const canSubmit = trimmedCaption.length > 0;
  const submitButtonLabel = isSubmitting
    ? t(
        imagePreview?.file
          ? "create_post_uploading"
          : "create_post_posting",
        {
          defaultValue: imagePreview?.file ? "Uploading..." : "Posting...",
        }
      )
    : t("Post");
  const disabledSubmitMessage = t("opinion_caption_required", {
    defaultValue: "Please add a caption to share your opinion.",
  });

  React.useEffect(() => {
    resetSubmissionState();

    return () => {
      resetSubmissionState(false);
    };
  }, [resetSubmissionState]);

  React.useEffect(() => {
    onDirtyChange?.(Boolean(caption.trim() || imagePreview?.file));
  }, [caption, imagePreview?.file, onDirtyChange]);

  React.useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange]
  );

  const handleImageUpload = (e, type = "image") => {
    const file = e.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setImagePreview({ url: previewUrl, file, type });
  };

  const handlePostSubmit = async () => {
    if (submitLockRef.current) {
      return;
    }

    if (!canSubmit) {
      alert(
        t("opinion_caption_required", {
          defaultValue: "Please add a caption to share your opinion.",
        })
      );
      return;
    }

    const token =
      typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
    if (!token && !getCsrfToken()) {
      alert(t("login_required", { defaultValue: "Please log in first." }));
      return;
    }

    submitLockRef.current = true;
    onSubmittingChange?.(true);
    setIsSubmitting(true);

    try {
      const form = new FormData();
      form.append("caption", trimmedCaption);
      if (token) {
        form.append("token", token);
      }
      form.append(
        "clientRequestId",
        getOrCreatePostSubmissionRequestId(clientRequestIdRef)
      );

      if (imagePreview?.file) {
        form.append("media", imagePreview.file);
      }

      const res = await fetch(
        `${API_BASE}/posts/opinion`,
        withApiCredentials({
          method: "POST",
          body: form,
        })
      );

      if (!res.ok) {
        throw new Error("Failed to save opinion post");
      }

      const savedPost = await res.json();

      const audio = new Audio(process.env.PUBLIC_URL + "/sound-efects/reel.mp3");
      audio.volume = 0.8;
      audio.play().catch(() => {});

      onAddPost(savedPost);
      resetSubmissionState();
      onClose({ force: true, reason: "submit_success" });
    } catch (err) {
      console.error(err);
      alert(
        t("opinion_save_failed", {
          defaultValue: "Could not save opinion post. Check backend server.",
        })
      );
    } finally {
      resetSubmissionState();
    }
  };

  return (
    <div className="opinion-form">
      <fieldset className="create-post-form-shell" disabled={isSubmitting}>
        <CreatePostSection
          title={t("create_post_section_caption_media")}
          className="create-post-section-card--top create-post-section-card--media create-post-section-card--opinion-media"
          icon={ImagePlus}
        >
          <label className="catch-form-caption-label opinion-form-label">
            {t("opinion_caption")}:
          </label>

          <textarea
            placeholder={t("opinion_caption_placeholder")}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="catch-form-caption-textarea opinion-form-textarea"
            disabled={isSubmitting}
          />

          <div className="catch-form-media-actions opinion-form-upload-buttons">
            <label
              htmlFor="opinionImageUpload"
              title={t("opinion_upload_image")}
              className={`catch-form-media-action-btn opinion-form-media-action-label${isSubmitting ? " is-disabled" : ""}`}
            >
              <ImagePlus size={16} strokeWidth={2.25} aria-hidden="true" />
              <span>{t("opinion_upload_image")}</span>
              <input
                id="opinionImageUpload"
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, "image")}
                className="opinion-form-hidden-input"
                disabled={isSubmitting}
              />
            </label>

            <label
              htmlFor="opinionVideoUpload"
              title={t("opinion_upload_video")}
              className={`catch-form-media-action-btn opinion-form-media-action-label${isSubmitting ? " is-disabled" : ""}`}
            >
              <Video size={16} strokeWidth={2.25} aria-hidden="true" />
              <span>{t("opinion_upload_video")}</span>
              <input
                id="opinionVideoUpload"
                type="file"
                accept="video/*"
                onChange={(e) => handleImageUpload(e, "video")}
                className="opinion-form-hidden-input"
                disabled={isSubmitting}
              />
            </label>
          </div>

          <div className="catch-form-media-helper opinion-form-media-helper">
            <Info size={16} strokeWidth={2.25} aria-hidden="true" />
            <span>{t("opinion_media_optional_hint")}</span>
          </div>

          {imagePreview && (
            <div className="catch-form-preview opinion-form-preview">
              {imagePreview.type === "image" ? (
                <img
                  src={imagePreview.url}
                  alt={t("catch_media_preview_alt")}
                  className="catch-form-preview-media opinion-form-preview-image"
                />
              ) : (
                <video
                  src={imagePreview.url}
                  controls
                  className="catch-form-preview-media opinion-form-preview-video"
                />
              )}
            </div>
          )}
        </CreatePostSection>

        <div className="opinion-form-actions">
          <button
            onClick={() => {
              onClose({
                reason: "user_cancel",
                onBeforeClose: () => resetSubmissionState(),
              });
            }}
            className="opinion-form-cancel-btn"
            disabled={isSubmitting}
          >
            {t("cancel")}
          </button>

          <div
            className={`catch-submit-tooltip-wrapper${canSubmit ? "" : " is-disabled"}`}
            title={canSubmit ? undefined : disabledSubmitMessage}
          >
            {!canSubmit && (
              <div className="catch-submit-tooltip" role="tooltip">
                {disabledSubmitMessage}
              </div>
            )}

            <span className="catch-submit-tooltip-trigger">
              <button
                onClick={handlePostSubmit}
                className={`opinion-form-submit-btn create-post-submit-btn${!canSubmit || isSubmitting ? " is-disabled" : ""}${isSubmitting ? " is-loading" : ""}`}
                disabled={!canSubmit || isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting && (
                  <span className="create-post-submit-spinner" aria-hidden="true" />
                )}
                {submitButtonLabel}
              </button>
            </span>
          </div>
        </div>
      </fieldset>
    </div>
  );
}

export default OpinionForm;
